import { useState } from "react";
import { ArrowUpDown, AlertTriangle, Package, ArrowRight, ChevronLeft, Search, Filter, Eye, RefreshCw } from 'lucide-react';

const TransferMaterialsPage = ({ onBack }) => {
    const [transferType, setTransferType] = useState('toInventory');
    const [selectedProject, setSelectedProject] = useState('');
    const [sourceProject, setSourceProject] = useState('');
    const [selectedMaterials, setSelectedMaterials] = useState([]);
    const [materialQuantities, setMaterialQuantities] = useState({});
    const [showWarning, setShowWarning] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBySource, setFilterBySource] = useState('all');

    // Mock data for available projects
    const availableProjects = [
        { id: 'warehouse', name: 'Main Warehouse', type: 'warehouse', location: 'Central Location' },
        { id: 'siteA', name: 'Construction Site A', type: 'project', location: 'Downtown' },
        { id: 'siteB', name: 'Construction Site B', type: 'project', location: 'Suburb' },
        { id: 'storage', name: 'Storage Unit C', type: 'warehouse', location: 'Industrial Zone' },
        { id: 'bridge', name: 'Bridge Project', type: 'project', location: 'Highway 101' }
    ];

    // Mock data for project materials
    const projectMaterials = [
        {
            id: 1,
            name: 'Steel Beam 200x100',
            productNumber: 'SB-200-100',
            quantity: 15,
            weight: 45.5,
            source: 'inventory',
            category: 'Structural',
            lastUsed: '2024-01-15'
        },
        {
            id: 2,
            name: 'Custom Frame Assembly',
            productNumber: 'CFA-001',
            quantity: 8,
            weight: 25.3,
            source: 'custom',
            category: 'Custom',
            lastUsed: '2024-01-10'
        },
        {
            id: 3,
            name: 'Aluminum Scaffold Tube',
            productNumber: 'AST-48-3',
            quantity: 25,
            weight: 3.2,
            source: 'inventory',
            category: 'Scaffolding',
            lastUsed: '2024-01-12'
        },
        {
            id: 4,
            name: 'Safety Clamp Heavy Duty',
            productNumber: 'SC-HD-01',
            quantity: 40,
            weight: 0.8,
            source: 'inventory',
            category: 'Safety',
            lastUsed: '2024-01-14'
        },
        {
            id: 5,
            name: 'Custom Connector Joint',
            productNumber: 'CCJ-002',
            quantity: 12,
            weight: 2.1,
            source: 'custom',
            category: 'Custom',
            lastUsed: '2024-01-08'
        }
    ];

    // Filter materials based on search and filter criteria
    const filteredMaterials = projectMaterials.filter(material => {
        const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            material.productNumber.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterBySource === 'all' || material.source === filterBySource;
        return matchesSearch && matchesFilter;
    });

    const handleMaterialSelection = (materialId, checked) => {
        if (checked) {
            setSelectedMaterials([...selectedMaterials, materialId]);
            // Set default quantity to full quantity
            const material = projectMaterials.find(m => m.id === materialId);
            setMaterialQuantities({
                ...materialQuantities,
                [materialId]: material.quantity
            });
        } else {
            setSelectedMaterials(selectedMaterials.filter(id => id !== materialId));
            const newQuantities = { ...materialQuantities };
            delete newQuantities[materialId];
            setMaterialQuantities(newQuantities);
        }
    };

    const handleQuantityChange = (materialId, quantity) => {
        const material = projectMaterials.find(m => m.id === materialId);
        const validQuantity = Math.min(Math.max(1, parseInt(quantity) || 1), material.quantity);
        setMaterialQuantities({
            ...materialQuantities,
            [materialId]: validQuantity
        });
    };

    const calculateTotalWeight = () => {
        return selectedMaterials.reduce((total, materialId) => {
            const material = projectMaterials.find(m => m.id === materialId);
            const quantity = materialQuantities[materialId] || 0;
            return total + (material.weight * quantity);
        }, 0);
    };

    const handleTransfer = () => {
        const hasCustomItems = selectedMaterials.some(id =>
            projectMaterials.find(m => m.id === id)?.source === 'custom'
        );

        if (hasCustomItems && transferType === 'toInventory') {
            setShowWarning(true);
        } else {
            // Perform transfer
            const totalWeight = calculateTotalWeight();
            const itemCount = selectedMaterials.length;
            alert(`Transfer successful!\n${itemCount} items (${totalWeight.toFixed(2)} kg) transferred.`);
            // Reset form
            setSelectedMaterials([]);
            setMaterialQuantities({});
            setSelectedProject('');
            setSourceProject('');
        }
    };

    const confirmTransfer = () => {
        const totalWeight = calculateTotalWeight();
        const itemCount = selectedMaterials.length;
        const customItems = selectedMaterials.filter(id =>
            projectMaterials.find(m => m.id === id)?.source === 'custom'
        ).length;

        alert(`Transfer successful!\n${itemCount} items (${totalWeight.toFixed(2)} kg) transferred.\n${customItems} custom items deleted.`);
        setShowWarning(false);
        setSelectedMaterials([]);
        setMaterialQuantities({});
        setSelectedProject('');
        setSourceProject('');
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <ArrowUpDown className="h-8 w-8 text-blue-600" />
                    <h1 className="text-3xl font-bold text-gray-900">Transfer Materials</h1>
                </div>
                <p className="text-gray-600">Move materials between projects or return them to inventory</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Transfer Configuration Panel */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-sm border p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transfer Configuration</h3>

                        {/* Transfer Type Selection */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Transfer Type
                            </label>
                            <div className="space-y-2">
                                <button
                                    onClick={() => setTransferType('toInventory')}
                                    className={`w-full px-4 py-3 rounded-lg border text-left transition-colors ${transferType === 'toInventory'
                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Package className="h-5 w-5" />
                                        <div>
                                            <div className="font-medium">Back to Inventory</div>
                                            <div className="text-xs text-gray-500">Return materials to warehouse</div>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => setTransferType('betweenProjects')}
                                    className={`w-full px-4 py-3 rounded-lg border text-left transition-colors ${transferType === 'betweenProjects'
                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <ArrowRight className="h-5 w-5" />
                                        <div>
                                            <div className="font-medium">Between Projects</div>
                                            <div className="text-xs text-gray-500">Move to another project</div>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Source Project Selection (for between projects) */}
                        {transferType === 'betweenProjects' && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Source Project
                                </label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={sourceProject}
                                    onChange={(e) => setSourceProject(e.target.value)}
                                >
                                    <option value="">Select source project</option>
                                    {availableProjects.map(project => (
                                        <option key={project.id} value={project.id}>
                                            {project.name} ({project.type})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Destination Project Selection */}
                        {transferType === 'betweenProjects' && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Destination Project
                                </label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={selectedProject}
                                    onChange={(e) => setSelectedProject(e.target.value)}
                                >
                                    <option value="">Select destination project</option>
                                    {availableProjects
                                        .filter(project => project.id !== sourceProject)
                                        .map(project => (
                                            <option key={project.id} value={project.id}>
                                                {project.name} ({project.type})
                                            </option>
                                        ))}
                                </select>
                            </div>
                        )}

                        {/* Transfer Summary */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3">Transfer Summary</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Selected Items:</span>
                                    <span className="font-medium">{selectedMaterials.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Weight:</span>
                                    <span className="font-medium">{calculateTotalWeight().toFixed(2)} kg</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Transfer Type:</span>
                                    <span className="font-medium">
                                        {transferType === 'toInventory' ? 'To Inventory' : 'Between Projects'}
                                    </span>
                                </div>
                                {transferType === 'betweenProjects' && selectedProject && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Destination:</span>
                                        <span className="font-medium text-xs">
                                            {availableProjects.find(p => p.id === selectedProject)?.name}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Materials Selection Panel */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow-sm border">
                        {/* Materials Header */}
                        <div className="p-6 border-b">
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900">Available Materials</h3>
                                <div className="flex gap-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                        <input
                                            type="text"
                                            placeholder="Search materials..."
                                            className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <select
                                        value={filterBySource}
                                        onChange={(e) => setFilterBySource(e.target.value)}
                                        className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="all">All Sources</option>
                                        <option value="inventory">Inventory Only</option>
                                        <option value="custom">Custom Only</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Materials List */}
                        <div className="p-6">
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {filteredMaterials.map(material => (
                                    <div key={material.id} className={`border rounded-lg p-4 transition-colors ${selectedMaterials.includes(material.id)
                                            ? 'border-blue-200 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedMaterials.includes(material.id)}
                                                onChange={(e) => handleMaterialSelection(material.id, e.target.checked)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                            />

                                            <div className="flex-1">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div>
                                                        <h4 className="font-medium text-gray-900">{material.name}</h4>
                                                        <p className="text-sm text-gray-600">{material.productNumber}</p>
                                                    </div>
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${material.source === 'custom'
                                                            ? 'bg-orange-100 text-orange-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        {material.source === 'custom' ? 'Custom' : 'Inventory'}
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                                                    <div>
                                                        <span className="font-medium">Available:</span> {material.quantity} units
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Weight:</span> {material.weight} kg each
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Category:</span> {material.category}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Last Used:</span> {material.lastUsed}
                                                    </div>
                                                </div>

                                                {selectedMaterials.includes(material.id) && (
                                                    <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
                                                        <label className="text-sm font-medium text-gray-700">
                                                            Transfer Quantity:
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={material.quantity}
                                                            value={materialQuantities[material.id] || material.quantity}
                                                            onChange={(e) => handleQuantityChange(material.id, e.target.value)}
                                                            className="w-24 px-3 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-500">
                                                            of {material.quantity} available
                                                        </span>
                                                        <span className="text-sm font-medium text-gray-700">
                                                            ({((materialQuantities[material.id] || material.quantity) * material.weight).toFixed(2)} kg)
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {filteredMaterials.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                        <p>No materials found matching your criteria</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="p-6 border-t bg-gray-50">
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setSelectedMaterials([]);
                                        setMaterialQuantities({});
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    <RefreshCw className="h-4 w-4 inline mr-2" />
                                    Clear Selection
                                </button>
                                <button
                                    onClick={handleTransfer}
                                    disabled={selectedMaterials.length === 0 || (transferType === 'betweenProjects' && !selectedProject)}
                                    className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Transfer {selectedMaterials.length} Material{selectedMaterials.length !== 1 ? 's' : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Warning Modal */}
            {showWarning && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <AlertTriangle className="h-6 w-6 text-yellow-500" />
                                <h3 className="text-lg font-semibold">Transfer Warning</h3>
                            </div>
                            <div className="mb-6">
                                <p className="text-gray-600 mb-4">
                                    Some selected materials are from custom lists. These items will be permanently deleted after transfer.
                                </p>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Items to be deleted:</strong>
                                    </p>
                                    <ul className="mt-2 text-sm text-yellow-700">
                                        {selectedMaterials
                                            .filter(id => projectMaterials.find(m => m.id === id)?.source === 'custom')
                                            .map(id => {
                                                const material = projectMaterials.find(m => m.id === id);
                                                return (
                                                    <li key={id}>• {material.name} ({materialQuantities[id] || material.quantity} units)</li>
                                                );
                                            })}
                                    </ul>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowWarning(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmTransfer}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Continue Transfer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransferMaterialsPage;